import React, { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { useSiteContent } from "../data/SiteContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CATERING_URL = "https://freshfastfoodsllc.com";
const DEFAULT_ORDER_URL =
  "https://www.grubhub.com/restaurant/burgers-tacos-wings--seafood-110-46-merrick-blvd-jamaica/2508348";

function classify(name) {
  const n = name.toLowerCase();
  if (/wing|chicken|whiting|fish|sauced|fried/.test(n)) return "meat";
  if (/cake|cheesecake|mousse|mascarpone|tiramisu|pie/.test(n)) return "dessert";
  if (/cookie/.test(n)) return "snack";
  if (/bagel|salad|fries|chips|side/.test(n)) return "side";
  if (/juice|coffee|tea|drink|water|soda|punch/.test(n)) return "drink";
  return "other";
}

function pickBalanced(items) {
  const buckets = { meat: [], side: [], dessert: [], cookie: [], drink: [], other: [] };
  items.forEach((it) => {
    const c = classify(it.name);
    const k = c === "snack" ? "cookie" : c;
    if (!buckets[k]) buckets.other.push(it);
    else buckets[k].push(it);
  });
  const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
  const taken = [];
  const used = new Set();
  const take = (pool) => {
    const candidate = shuffle(pool).find((it) => !used.has(it.name));
    if (candidate) {
      taken.push(candidate);
      used.add(candidate.name);
      return true;
    }
    return false;
  };
  take(buckets.meat);
  take(buckets.side);
  take(buckets.dessert);
  if (!take(buckets.cookie)) take(buckets.dessert);
  if (taken.length < 4) {
    const rest = shuffle(items).filter((it) => !used.has(it.name));
    while (taken.length < 4 && rest.length) {
      taken.push(rest.shift());
    }
  }
  return taken;
}

// Build a deep link for a catering item.  If the scraper captured an explicit
// product URL, use it; otherwise fall back to a per-item search query against
// freshfastfoodsllc.com so customers still land near the right product.
function itemHref(item) {
  if (item.url && /^https?:\/\//i.test(item.url)) return item.url;
  const q = encodeURIComponent(item.name || "").replace(/%20/g, "+");
  return `${CATERING_URL}?search=${q}`;
}

export default function Catering() {
  const [items, setItems] = useState([]);
  const { content } = useSiteContent();
  const ORDER_URL = content?.links?.order_url || DEFAULT_ORDER_URL;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/catering-menu`);
        if (!alive) return;
        const all = data.items || [];
        setItems(pickBalanced(all));
      } catch (e) {
        console.error("catering fetch failed", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section
      id="catering"
      data-testid="catering-section"
      className="bg-[#0B0B0B] text-white py-12 sm:py-24"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-5">
          <p
            className="text-xs sm:text-sm font-extrabold tracking-[0.4em] uppercase"
            style={{ color: "#A3D900" }}
          >
            FEED THE CREW
          </p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">
            Catering &
            <br />
            Group Meals
          </h2>
          <p className="mt-5 text-white/70 text-base sm:text-lg max-w-md leading-relaxed">
            <strong className="text-white">Same-day and next-day pickup</strong> from our in-store menu —
            <span className="text-[#A3D900] font-semibold"> order online and pick up at our Jamaica, Queens shop</span>.
          </p>
          <p className="mt-3 text-white/65 text-sm sm:text-base max-w-md leading-relaxed">
            Hosting a larger private event? Our sister brand <strong className="text-white">Fresh Fast Foods</strong> handles full-service / staffed catering — custom menus, delivery, on-site cooking and setup.
          </p>
          <p className="mt-3 text-white/55 text-xs sm:text-sm max-w-md leading-relaxed italic">
            Perfect for corporate events, schools, government agencies, and activations.
          </p>

          <div className="mt-8 flex flex-nowrap items-stretch gap-2 sm:gap-3">
            <a
              href={CATERING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="full-service-catering-btn"
              className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.12em] sm:tracking-[0.16em] uppercase px-3 sm:px-5 lg:px-6 py-2.5 sm:py-3 text-[10px] sm:text-xs whitespace-nowrap transition-colors shadow-[0_8px_30px_rgba(163,217,0,0.25)]"
            >
              <span className="sm:hidden">Full Service</span>
              <span className="hidden sm:inline">Full-Service Catering</span>
              <ArrowRight size={12} className="hidden sm:inline transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="/order"
              data-testid="start-pickup-order-btn"
              className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-[#5FBDD6] bg-transparent text-[#5FBDD6] hover:bg-[#5FBDD6] hover:text-[#0B0B0B] font-extrabold tracking-[0.12em] sm:tracking-[0.16em] uppercase px-3 sm:px-5 lg:px-6 py-2.5 sm:py-3 text-[10px] sm:text-xs whitespace-nowrap transition-all"
            >
              Start Pickup Order
              <ArrowRight size={12} className="hidden sm:inline transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          <p className="mt-4 text-xs text-white/50">
            BTW pickup menu synced live · Full-service via freshfastfoodsllc.com
          </p>
        </div>

        <div className="lg:col-span-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-md sm:max-w-none mx-auto sm:mx-0 w-full">
          {items.map((item) => (
            <a
              key={item.name}
              href={itemHref(item)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`catering-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group bg-white text-[#0B0B0B] rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:shadow-[0_18px_40px_rgba(0,0,0,0.5)] hover:-translate-y-1 transition-all flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#0B0B0B]">
                <img
                  src={item.img}
                  alt={item.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="px-2.5 pt-2.5 pb-3 text-center">
                <h3 className="font-display text-sm sm:text-base uppercase tracking-tight leading-tight line-clamp-2">
                  {item.name}
                </h3>
                {item.desc && (
                  <p className="text-[10px] text-black/55 mt-0.5 leading-snug line-clamp-1">
                    {item.desc}
                  </p>
                )}
                <div
                  className="font-display text-lg sm:text-xl mt-1.5"
                  style={{ color: "#5FBDD6" }}
                >
                  {item.price}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

